import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { fetchWithAuth } from '../utils/api'
import { User } from '../types'
import TaskForm from './TaskForm'
import TaskDetail from './TaskDetail'
import TaskMap from './TaskMap'
import TaskHistory from './TaskHistory'

interface Task {
  id: number
  title: string
  description: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  status: 'suggested' | 'pending_approval' | 'approved' | 'assigned' | 'in_progress' | 'completed' | 'verified' | 'cancelled' | 'on_hold' | 'needs_revision'
  created_by: number
  created_by_name: string
  assigned_to: number | null
  assigned_to_name: string | null
  site_id: number | null
  site_name: string | null
  area_id: number | null
  area_name: string | null
  location_coords: [number, number] | null
  estimated_hours: number | null
  actual_hours: number | null
  due_date: string | null
  completed_at: string | null
  verified_at: string | null
  comment_count: number
  created_at: string
  updated_at: string
}

interface TaskDashboardProps {
  user: User
}

function TaskDashboard({ user }: TaskDashboardProps) {
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [assignedToFilter] = useState('')
  const [myTasksOnly, setMyTasksOnly] = useState(false)
  const [showTaskForm, setShowTaskForm] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null)
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null)
  const [showMapView, setShowMapView] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  useEffect(() => {
    fetchTasks()
  }, [statusFilter, priorityFilter, assignedToFilter, myTasksOnly])

  // Open create form if URL contains ?new=1 (from dashboard shortcut)
  useEffect(() => {
    const newParam = (searchParams.get('new') || '').toLowerCase()
    if (newParam === '1' || newParam === 'true') {
      setShowTaskForm(true)
      // Clean the URL so refreshing doesn't reopen the form
      const next = new URLSearchParams(searchParams)
      next.delete('new')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchTasks = async () => {
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (priorityFilter) params.append('priority', priorityFilter)
      if (assignedToFilter) params.append('assigned_to', assignedToFilter)
      if (myTasksOnly) params.append('my_tasks', 'true')

      const response = await fetchWithAuth(`/api/tasks?${params.toString()}`)
      if (!response.ok) {
        throw new Error('Failed to fetch tasks')
      }
      const data = await response.json()
      setTasks(data.tasks)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks')
    } finally {
      setLoading(false)
    }
  }

  const assignTaskToMe = async (taskId: number) => {
    try {
      const response = await fetchWithAuth(`/api/tasks/${taskId}/assign-to-me`, {
        method: 'POST'
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to assign task')
      }

      // Refresh tasks
      fetchTasks()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign task')
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#e74c3c'
      case 'high': return '#f39c12'
      case 'medium': return '#3498db'
      case 'low': return '#95a5a6'
      default: return '#95a5a6'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#27ae60'
      case 'verified': return '#2ecc71'
      case 'in_progress': return '#3498db'
      case 'assigned': return '#9b59b6'
      case 'approved': return '#1abc9c'
      case 'pending_approval': return '#f39c12'
      case 'suggested': return '#95a5a6'
      case 'cancelled': return '#e74c3c'
      case 'on_hold': return '#e67e22'
      case 'needs_revision': return '#e74c3c'
      default: return '#95a5a6'
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toISOString().slice(0, 19).replace('T', ' ')
  }

  const formatTime = (hours: number | null) => {
    if (!hours) return '-'
    return `${hours}h`
  }

  if (loading) {
    return <div className="loading">Loading tasks...</div>
  }

  return (
    <div className="task-dashboard">
      <div style={{ marginBottom: '1rem' }}>
        <button 
          className="btn" 
          onClick={() => window.location.href = `/app${window.location.search}`}
        >
          ← Back to User View
        </button>
      </div>

      <div className="dashboard-header">
        <h2>📋 Task Dashboard</h2>
        <div className="dashboard-actions">
          <button 
            className="btn btn-success"
            onClick={() => setShowTaskForm(true)}
          >
            ➕ New Task
          </button>
          <button 
            className="btn"
            onClick={() => setShowMapView(true)}
          >
            🗺️ Map View
          </button>
          <button 
            className="btn"
            onClick={() => setShowHistory(true)}
          >
            📚 History
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Filters */}
      <div className="filters-container">
        <div className="filter-group">
          <label>Status:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="suggested">Suggested</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="assigned">Assigned</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="verified">Verified</option>
            <option value="on_hold">On Hold</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Priority:</label>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
            <option value="">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        <div className="filter-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={myTasksOnly}
              onChange={(e) => setMyTasksOnly(e.target.checked)}
            />
            <span>My Tasks Only</span>
          </label>
        </div>
      </div>

      {/* Task Statistics */}
      <div className="task-stats">
        <div className="stat-card stat-active">
          <div className="stat-number">{tasks.filter(t => ['in_progress', 'assigned'].includes(t.status)).length}</div>
          <div className="stat-label">Active Tasks</div>
        </div>
        <div className="stat-card stat-pending">
          <div className="stat-number">{tasks.filter(t => ['suggested', 'pending_approval', 'approved'].includes(t.status)).length}</div>
          <div className="stat-label">Pending Tasks</div>
        </div>
        <div className="stat-card stat-completed">
          <div className="stat-number">{tasks.filter(t => ['completed', 'verified'].includes(t.status)).length}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card stat-overdue">
          <div className="stat-number">{tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && !['completed', 'verified', 'cancelled'].includes(t.status)).length}</div>
          <div className="stat-label">Overdue</div>
        </div>
        <div className="stat-card stat-on-hold">
          <div className="stat-number">{tasks.filter(t => ['on_hold', 'cancelled'].includes(t.status)).length}</div>
          <div className="stat-label">On Hold / Cancelled</div>
        </div>
      </div>

      {/* Tasks Table */}
      <div className="table-container">
        {tasks.length === 0 ? (
          <div className="loading">No tasks found</div>
        ) : (
          <table className="table tasks-table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Location</th>
                <th>Due Date</th>
                <th>Est. Time</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id} className="task-row">
                  <td>
                    <div className="task-title">{task.title}</div>
                    {task.description && (
                      <div className="task-description">{task.description}</div>
                    )}
                    {task.comment_count > 0 && (
                      <div className="task-comments">💬 {task.comment_count} comments</div>
                    )}
                  </td>
                  <td>
                    <span 
                      className="priority-badge" 
                      style={{ backgroundColor: getPriorityColor(task.priority) }}
                    >
                      {task.priority}
                    </span>
                  </td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(task.status) }}
                    >
                      {task.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    {task.assigned_to_name || (
                      <span className="unassigned">Unassigned</span>
                    )}
                  </td>
                  <td>
                    {task.site_name && (
                      <div className="location-info">
                        <div className="site-name">🏠 {task.site_name}</div>
                        {task.area_name && (
                          <div className="area-name">📍 {task.area_name}</div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="date-cell">{formatDate(task.due_date)}</td>
                  <td>{formatTime(task.estimated_hours)}</td>
                  <td>
                    <div className="task-actions">
                      <button 
                        className="btn btn-sm"
                        onClick={() => setSelectedTaskId(task.id)}
                      >
                        View
                      </button>
                      {(user.isAdmin || user.role === 'farm_manager' || user.id === task.created_by || user.id === task.assigned_to) && (
                        <button 
                          className="btn btn-sm btn-edit"
                          onClick={() => setEditingTaskId(task.id)}
                        >
                          Edit
                        </button>
                      )}
                      {!task.assigned_to && (
                        <button 
                          className="btn btn-sm btn-assign"
                          onClick={() => assignTaskToMe(task.id)}
                          title="Assign this task to yourself"
                        >
                          Assign to Me
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showTaskForm && (
        <TaskForm
          user={user}
          onSubmit={() => {
            setShowTaskForm(false)
            fetchTasks() // Refresh the task list
          }}
          onCancel={() => setShowTaskForm(false)}
        />
      )}

      {editingTaskId && (
        <TaskForm
          user={user}
          isEditing={true}
          taskId={editingTaskId}
          initialData={tasks.find(t => t.id === editingTaskId)}
          onSubmit={() => {
            setEditingTaskId(null)
            fetchTasks() // Refresh the task list
          }}
          onCancel={() => setEditingTaskId(null)}
        />
      )}

      {selectedTaskId && (
        <TaskDetail
          taskId={selectedTaskId}
          user={user}
          onClose={() => setSelectedTaskId(null)}
          onUpdate={() => fetchTasks()}
        />
      )}

      {showMapView && (
        <TaskMap
          user={user}
          onClose={() => setShowMapView(false)}
          onTaskSelect={(taskId) => {
            setSelectedTaskId(taskId)
            setShowMapView(false)
          }}
        />
      )}

      {showHistory && (
        <TaskHistory
          user={user}
          onClose={() => setShowHistory(false)}
          onTaskSelect={(taskId) => {
            setSelectedTaskId(taskId)
            setShowHistory(false)
          }}
        />
      )}
    </div>
  )
}

export default TaskDashboard
